import { addressFor, type AddressWords } from './address';
import { alt, grandAddress, indicLabel, pick, type IndicWords, type MF } from './indic';
import type { Kin, KinPack, KinStep, Sex } from './types';

/**
 * Arabic relationship words (PRD 7.2.3), in Arabic script and in English letters. Beta until an Arabic speaker
 * has reviewed them (Q-08). Modern Standard Arabic, which every Arab country reads; everyday words differ by
 * region and can be replaced per family once custom words arrive (K5).
 *
 * Like the Indian languages, Arabic names relatives by side: a father's brother is ʿamm, a mother's brother
 * khāl, and their children ibn al-ʿamm and ibn al-khāl. So the shared rules in `indic.ts` give most labels.
 * Arabic differs where its words are built as a chain of nouns ("the wife of the son of the uncle"), where a
 * half-brother is named by the parent he shares (akh li-ab, akh li-umm), and in the "how you're related" line,
 * which runs the other way round from English: ibn akhī wālidik, "the son of the brother of your father".
 */

interface Lex {
  latin: boolean;
  words: IndicWords;
  /** Nouns for the "how you're related" line and for words built from parts. */
  parent: MF;
  father: MF; // "the father" / "the mother" at the end of a chain: al-ab, al-umm
  child: MF;
  sibling: MF;
  spouse: MF;
  /** A half-sibling, by the parent they share: [father, mother, unknown]. */
  half: [string, string, string];
  you: MF;
  /** Forms of address (PRD 7.2.4): "my uncle" in Modern Standard Arabic, then the everyday ʿammo, khalto. */
  address: AddressWords;
}

const g4 = (m: string[], f: string[]) => grandAddress([m, f, m, f], [m, f, m, f]);

const ar: IndicWords = {
  you: 'أنت',
  relative: 'من الأقارب',
  spouse: { m: 'زوج', f: 'زوجة' },
  parent: { m: 'أب', f: 'أم' },
  child: { m: 'ابن', f: 'ابنة' },
  brother: { elder: 'أخ', younger: 'أخ', any: 'أخ' },
  sister: { elder: 'أخت', younger: 'أخت', any: 'أخت' },
  grandF: { m: 'جد', f: 'جدة' },
  grandM: { m: 'جد', f: 'جدة' },
  greatF: { m: 'الجد الأكبر', f: 'الجدة الكبرى' },
  greatM: { m: 'الجد الأكبر', f: 'الجدة الكبرى' },
  ancestor: 'من الأجداد',
  grandchildViaSon: { m: 'حفيد', f: 'حفيدة' },
  grandchildViaDaughter: { m: 'حفيد', f: 'حفيدة' },
  greatGrandchild: { m: 'ابن الحفيد', f: 'ابنة الحفيد' },
  descendant: 'من الأحفاد',
  uncle: { FB: { elder: 'عم', any: 'عم' }, FZ: { elder: 'عمة', any: 'عمة' }, MB: { elder: 'خال', any: 'خال' }, MZ: { elder: 'خالة', any: 'خالة' } },
  uncleSpouse: {
    FB: { elder: 'زوجة العم', any: 'زوجة العم' },
    FZ: { elder: 'زوج العمة', any: 'زوج العمة' },
    MB: { elder: 'زوجة الخال', any: 'زوجة الخال' },
    MZ: { elder: 'زوج الخالة', any: 'زوج الخالة' },
  },
  cousin: (kind, sex) => {
    const of = { FB: 'العم', FZ: 'العمة', MB: 'الخال', MZ: 'الخالة' }[kind];
    return pick({ m: `ابن ${of}`, f: `بنت ${of}` }, sex);
  },
  distantCousin: { m: 'قريب', f: 'قريبة' },
  nephewViaBrother: { m: 'ابن الأخ', f: 'بنت الأخ' },
  nephewViaSister: { m: 'ابن الأخت', f: 'بنت الأخت' },
  // The in-law words below are used only for a spouse's brother's or sister's spouse; the rest of a spouse's
  // family is built from parts in `spouseSide`.
  parentInLaw: { m: 'حمو', f: 'حماة' },
  grandparentInLaw: { m: 'جد', f: 'جدة' },
  spouseUncle: () => 'من الأقارب',
  husbandBrother: { elder: 'أخو الزوج', younger: 'أخو الزوج', any: 'أخو الزوج' },
  husbandSister: 'أخت الزوج',
  wifeBrother: 'أخو الزوجة',
  wifeSister: 'أخت الزوجة',
  husbandBrotherWife: { elder: 'سلفة', younger: 'سلفة', any: 'سلفة' },
  husbandSisterHusband: 'زوج أخت الزوج',
  wifeSisterHusband: 'زوج أخت الزوجة', // not ʿadīl: Wehr gives it as a sister's husband (Egyptian), others as a wife's sister's
  wifeBrotherWife: 'زوجة أخي الزوجة',
  brotherWife: 'زوجة الأخ',
  sisterHusband: { elder: 'زوج الأخت', younger: 'زوج الأخت', any: 'زوج الأخت' },
  childInLaw: { m: 'صهر', f: 'كنة' },
  coParentInLaw: { m: 'والد', f: 'والدة' }, // built from parts in `label`
  stepParent: { m: 'زوج الأم', f: 'زوجة الأب' },
  stepChild: { m: 'ربيب', f: 'ربيبة' },
  stepSibling: { m: 'ابن', f: 'ابنة' }, // built from parts in `label`
};

const arLatn: IndicWords = {
  you: 'Anta/Anti',
  relative: 'Min al-Aqarib',
  spouse: { m: 'Zawj', f: 'Zawja' },
  parent: { m: 'Ab', f: 'Umm' },
  child: { m: 'Ibn', f: 'Ibna' },
  brother: { elder: 'Akh', younger: 'Akh', any: 'Akh' },
  sister: { elder: 'Ukht', younger: 'Ukht', any: 'Ukht' },
  grandF: { m: 'Jadd', f: 'Jadda' },
  grandM: { m: 'Jadd', f: 'Jadda' },
  greatF: { m: 'Al-Jadd al-Akbar', f: 'Al-Jadda al-Kubra' },
  greatM: { m: 'Al-Jadd al-Akbar', f: 'Al-Jadda al-Kubra' },
  ancestor: 'Min al-Ajdad',
  grandchildViaSon: { m: 'Hafid', f: 'Hafida' },
  grandchildViaDaughter: { m: 'Hafid', f: 'Hafida' },
  greatGrandchild: { m: 'Ibn al-Hafid', f: 'Ibnat al-Hafid' },
  descendant: 'Min al-Ahfad',
  uncle: { FB: { elder: 'Amm', any: 'Amm' }, FZ: { elder: 'Amma', any: 'Amma' }, MB: { elder: 'Khal', any: 'Khal' }, MZ: { elder: 'Khala', any: 'Khala' } },
  uncleSpouse: {
    FB: { elder: 'Zawjat al-Amm', any: 'Zawjat al-Amm' },
    FZ: { elder: 'Zawj al-Amma', any: 'Zawj al-Amma' },
    MB: { elder: 'Zawjat al-Khal', any: 'Zawjat al-Khal' },
    MZ: { elder: 'Zawj al-Khala', any: 'Zawj al-Khala' },
  },
  cousin: (kind, sex) => {
    const of = { FB: 'al-Amm', FZ: 'al-Amma', MB: 'al-Khal', MZ: 'al-Khala' }[kind];
    return pick({ m: `Ibn ${of}`, f: `Bint ${of}` }, sex);
  },
  distantCousin: { m: 'Qarib', f: 'Qariba' },
  nephewViaBrother: { m: 'Ibn al-Akh', f: 'Bint al-Akh' },
  nephewViaSister: { m: 'Ibn al-Ukht', f: 'Bint al-Ukht' },
  parentInLaw: { m: 'Hamu', f: 'Hama' },
  grandparentInLaw: { m: 'Jadd', f: 'Jadda' },
  spouseUncle: () => 'Min al-Aqarib',
  husbandBrother: { elder: 'Akhu al-Zawj', younger: 'Akhu al-Zawj', any: 'Akhu al-Zawj' },
  husbandSister: 'Ukht al-Zawj',
  wifeBrother: 'Akhu al-Zawja',
  wifeSister: 'Ukht al-Zawja',
  husbandBrotherWife: { elder: 'Silfa', younger: 'Silfa', any: 'Silfa' },
  husbandSisterHusband: 'Zawj Ukht al-Zawj',
  wifeSisterHusband: 'Zawj Ukht al-Zawja',
  wifeBrotherWife: 'Zawjat Akhi al-Zawja',
  brotherWife: 'Zawjat al-Akh',
  sisterHusband: { elder: 'Zawj al-Ukht', younger: 'Zawj al-Ukht', any: 'Zawj al-Ukht' },
  childInLaw: { m: 'Sihr', f: 'Kanna' },
  coParentInLaw: { m: 'Walid', f: 'Walida' },
  stepParent: { m: 'Zawj al-Umm', f: 'Zawjat al-Ab' },
  stepChild: { m: 'Rabib', f: 'Rabiba' },
  stepSibling: { m: 'Ibn', f: 'Ibna' },
};

const AR: Lex = {
  latin: false,
  words: ar,
  parent: { m: 'والد', f: 'والدة' },
  father: { m: 'أب', f: 'أم' },
  child: { m: 'ابن', f: 'ابنة' },
  sibling: { m: 'أخ', f: 'أخت' },
  spouse: { m: 'زوج', f: 'زوجة' },
  half: ['لأب', 'لأم', 'غير شقيق'],
  you: { m: 'أنت', f: 'أنت' },
  address: {
    nameScript: /^\p{Script=Arabic}+$/u,
    parent: { m: ['أبي', 'بابا'], f: ['أمي', 'ماما'] },
    // Syria: Sidi, Jeddi, Jeddo; Sitti, Jedditi, Teitei (Suryanarayan & Khalil 2021, p.130); Egypt: sitt (Badawi & Hinds).
    grandparent: g4(['جدي', 'جدو', 'سيدي'], ['جدتي', 'تيتا', 'ستي']),
    elder: { m: [], f: [] },
    uncle: { FB: { any: ['عمي', 'عمو'] }, FZ: { any: ['عمتي', 'عمتو'] }, MB: { any: ['خالي', 'خالو'] }, MZ: { any: ['خالتي', 'خالتو'] } },
    // Syrian usage (Suryanarayan & Khalil 2021, p.134): a father's brother's wife is ʿamti, a mother's brother's
    // wife khalti, an aunt's husband ʿammi; a father-in-law ʿammi, a mother-in-law khalti or ʿamti.
    uncleSpouse: { FB: { any: ['عمتي', 'عمتو'] }, FZ: { any: ['عمي', 'عمو'] }, MB: { any: ['خالتي', 'خالتو'] }, MZ: { any: ['عمي', 'عمو'] } },
    parentInLaw: { m: ['عمي'], f: ['خالتي', 'عمتي'] },
    elderSiblingSpouse: { m: [], f: [] },
    coParentInLaw: { m: [], f: [] },
  },
};

const AR_LATN: Lex = {
  latin: true,
  words: arLatn,
  parent: { m: 'Walid', f: 'Walida' },
  father: { m: 'Ab', f: 'Umm' },
  child: { m: 'Ibn', f: 'Ibna' },
  sibling: { m: 'Akh', f: 'Ukht' },
  spouse: { m: 'Zawj', f: 'Zawja' },
  half: ['li-Ab', 'li-Umm', 'ghayr Shaqiq'],
  you: { m: 'Anta', f: 'Anti' },
  address: {
    parent: { m: ['Abi', 'Baba'], f: ['Ummi', 'Mama'] },
    grandparent: g4(['Jaddi', 'Jiddo', 'Sidi'], ['Jadditi', 'Teta', 'Sitti']),
    elder: { m: [], f: [] },
    uncle: {
      FB: { any: ['Ammi', 'Ammo {name}'] },
      FZ: { any: ['Amti', 'Ammto {name}'] },
      MB: { any: ['Khali', 'Khalo {name}'] },
      MZ: { any: ['Khalti', 'Khalto {name}'] },
    },
    uncleSpouse: {
      FB: { any: ['Amti', 'Ammto {name}'] },
      FZ: { any: ['Ammi', 'Ammo {name}'] },
      MB: { any: ['Khalti', 'Khalto {name}'] },
      MZ: { any: ['Ammi', 'Ammo {name}'] },
    },
    parentInLaw: { m: ['Ammi'], f: ['Khalti', 'Amti'] },
    elderSiblingSpouse: { m: [], f: [] },
    coParentInLaw: { m: [], f: [] },
  },
};

/** Both forms when gender is unknown, else the one. */
const forms = (w: MF, sex: Sex): string[] => (sex === 'f' ? [w.f] : sex === 'm' ? [w.m] : [...new Set([w.m, w.f])]);

/**
 * A noun followed by another noun (the iḍāfa construct). In Arabic script only akh changes, to akhū; in English
 * letters a feminine -a becomes -at (zawja → zawjat). `genitive`: the noun itself follows another (akhī).
 */
function construct(lex: Lex, word: string, genitive = false): string {
  if (lex.latin) {
    if (word.toLowerCase() === 'akh') return `${word}${genitive ? 'i' : 'u'}`;
    return word.endsWith('a') ? `${word}t` : word;
  }
  if (word === 'أخ') return genitive ? 'أخي' : 'أخو';
  return word;
}

/** "The X": a one-word noun takes the article; a phrase already ends with one ("ibn al-ʿamm"). */
function definite(lex: Lex, phrase: string): string {
  if (phrase.includes(' ')) return phrase;
  return lex.latin ? `al-${phrase}` : `ال${phrase}`;
}

/** Every combination of the alternatives, each joined into one phrase: [[a, b], [c]] → "a c/b c". */
function chains(parts: string[][]): string {
  const out = parts.reduce<string[]>((acc, alts) => acc.flatMap((a) => alts.map((b) => (a ? `${a} ${b}` : b))), ['']);
  return alt(...out);
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Head of X" for each alternative of a label, e.g. ["zawjat"] + "ibn al-ʿamm/bint al-ʿamm". */
function of(lex: Lex, heads: string[], label: string): string {
  return chains([heads.map((h) => construct(lex, h)), label.split('/').map((l) => definite(lex, l))]);
}

/** The blood relative at the end of `steps`, named as the viewer of `steps` would name them. */
function bloodLabel(lex: Lex, steps: KinStep[], shape: Kin['shape']): string {
  const last = steps[steps.length - 1]!;
  return indicLabel(lex.words, { kind: 'blood', via: 'blood', shape, path: steps, key: '', half: false, sex: last.sex, meSex: null, older: null, relativeOlder: null });
}

/** My spouse's family: "the wife's grandfather", "the husband's uncle" (jadd al-zawja, ʿamm al-zawj). */
function spouseSide(lex: Lex, k: Kin): string {
  const [spouse, ...rest] = k.path as [KinStep, ...KinStep[]];
  const shape = k.shape!;
  const w = lex.words;
  if ('up' in shape && shape.up === 1) return pick(w.parentInLaw, k.sex);
  if ('down' in shape) return shape.down === 1 ? pick(w.stepChild, k.sex) : bloodLabel(lex, rest, shape);
  const label = bloodLabel(lex, rest, shape);
  if (!('up' in shape) && shape.b - shape.a >= 1) return label; // a spouse's nephews and nieces: as one's own
  // One-word relatives (grandparents, uncles and aunts, brothers and sisters) take "of the husband/wife".
  if (label.split('/').some((l) => l.includes(' '))) return 'up' in shape ? w.ancestor : w.relative;
  const theSpouse = forms(lex.spouse, spouse.sex).map((s) => definite(lex, s));
  return chains([label.split('/').map((l) => construct(lex, l)), theSpouse]);
}

/** My relatives' spouses: special words where Arabic has them, else "the wife of" the relative. */
function relativesSpouse(lex: Lex, k: Kin): string {
  const shape = k.shape!;
  const rel = k.path.slice(0, -1);
  const fallback = indicLabel(lex.words, k);
  if ('up' in shape) return fallback; // step-parents, grandparents
  if ('down' in shape && shape.down === 1) return fallback; // son-in-law, daughter-in-law
  if ('a' in shape) {
    const g = shape.b - shape.a;
    if (g < 0 || (g === 0 && shape.a === 1)) return fallback; // uncles' and aunts' spouses, brothers' wives
  }
  const blood = bloodLabel(lex, rel, shape);
  const w = lex.words;
  if ([w.descendant, w.ancestor, w.relative].includes(blood)) return w.relative;
  return of(lex, forms(lex.spouse, k.sex), blood);
}

/** A brother or sister, or a half-brother or half-sister named by the parent they share. */
function sibling(lex: Lex, step: KinStep): string {
  const base = forms(lex.sibling, step.sex);
  if (!step.half) return alt(...base);
  const [viaFather, viaMother, unknown] = lex.half;
  const via = step.halfVia === 'm' ? viaFather : step.halfVia === 'f' ? viaMother : unknown;
  return alt(...base.map((b) => `${b} ${via}`));
}

function label(lex: Lex, k: Kin): string {
  const [p0, p1] = k.path;
  switch (k.kind) {
    case 'self':
      return pick(lex.you, k.meSex);
    case 'blood':
      if ('a' in k.shape! && k.shape.a === 1 && k.shape.b === 1) return sibling(lex, p0!);
      return indicLabel(lex.words, k);
    case 'marriage':
      if (k.via === 'mine') return spouseSide(lex, k);
      if (k.via === 'theirs') return relativesSpouse(lex, k);
      return indicLabel(lex.words, k);
    case 'coParentInLaw': {
      // [my child, their spouse, that spouse's parent]: "the father of the wife of the son".
      return chains([forms(lex.parent, k.sex).map((x) => construct(lex, x)), forms(lex.spouse, p1!.sex).map((x) => construct(lex, x)), forms(lex.child, p0!.sex).map((x) => definite(lex, x))]);
    }
    case 'stepSibling': {
      // [my parent, their spouse, that spouse's child]: "the son of the wife of the father".
      return chains([forms(lex.child, k.sex).map((x) => construct(lex, x)), forms(lex.spouse, p1!.sex).map((x) => construct(lex, x)), forms(lex.father, p0!.sex).map((x) => definite(lex, x))]);
    }
    default:
      return indicLabel(lex.words, k);
  }
}

/** "Your" on the last noun: -ka / -ki, written ك in Arabic script; a final ة is written ت before it. */
function withYour(lex: Lex, word: string, meSex: Sex): string {
  if (!lex.latin) return `${word.endsWith('ة') ? word.slice(0, -1) + 'ت' : word}ك`;
  const stem = word.endsWith('a') ? `${word}t` : word;
  const vowel = /[aeiou]$/.test(stem);
  if (meSex === 'f') return `${stem}${vowel ? 'ki' : 'ik'}`;
  return `${stem}${vowel ? 'k' : 'ak'}`;
}

/**
 * "How you're related", outermost noun first: the son of the brother of your father (ibn akhī wālidik). Each noun
 * but the last is followed by another; the last carries "your".
 */
function explain(lex: Lex, k: Kin): string {
  if (k.path.length < 2) return '';
  const table = { parent: lex.parent, child: lex.child, sibling: lex.sibling, spouse: lex.spouse };
  const nouns = [...k.path].reverse().map((s, i, all) =>
    forms(table[s.rel], s.sex).map((word) => {
      const last = i === all.length - 1;
      const w = lex.latin ? word.toLowerCase() : word;
      const c = construct(lex, w, i > 0);
      return last ? withYour(lex, c, k.meSex) : c;
    }),
  );
  const out = chains(nouns);
  return lex.latin ? capital(out) : out;
}

function generation(lex: Lex, delta: number): string {
  const L = lex.latin;
  if (delta === 0) return L ? 'Jilak' : 'جيلك';
  if (delta === -1) return L ? 'Al-Walidan' : 'الوالدان';
  if (delta === 1) return L ? 'Al-Abna' : 'الأبناء';
  const n = Math.abs(delta);
  const when = delta < 0 ? (L ? 'Qabl' : 'قبل') : L ? 'Baad' : 'بعد';
  if (n === 2) return `${when} ${L ? 'jilayn' : 'جيلين'}`;
  // The counted noun follows the number's last part (Ryding, ch. 15): plural after 3–10, accusative singular
  // after 11–99, genitive singular after a whole hundred.
  const r = n >= 100 ? n % 100 : n;
  const noun = r >= 3 && r <= 10 ? (L ? 'ajyal' : 'أجيال') : r >= 11 ? (L ? 'jilan' : 'جيلًا') : L ? 'jil' : 'جيل';
  return `${when} ${n} ${noun}`;
}

function arabicPack(meta: Pick<KinPack, 'id' | 'name' | 'lang' | 'dir'>, lex: Lex): KinPack {
  return {
    ...meta,
    status: 'beta',
    label: (k) => label(lex, k),
    generation: (delta) => generation(lex, delta),
    explain: (k) => explain(lex, k),
    address: (k, name) => addressFor(lex.address, k, name),
  };
}

export const arPack = arabicPack({ id: 'ar', name: 'العربية', lang: 'ar', dir: 'rtl' }, AR);
export const arLatnPack = arabicPack({ id: 'ar-Latn', name: 'Arabic (amm, khala)', lang: 'ar-Latn', dir: 'ltr' }, AR_LATN);
